import { useState, type ReactElement } from "react";
import { Btn, ConfirmDialog, Icon } from "@v0y4ge/jrni-ui";
import type { NavApi } from "../../shell/router";
import type { UIEvent } from "../../lib/events-adapter";
import { can } from "../../shell/access";
import { useT } from "../../lib/i18n";
import { eventActions, serverMessage } from "./eventActions";

/**
 * Which lifecycle actions an event offers, from its state (operator, 2026-10-12):
 *  - Delete draft: a draft with no booked guests (discard refuses a booked draft).
 *  - Cancel: an event that has not ended, is not cancelled and not archived.
 *  - Archive: an event that has ended, or an upcoming one with no booked guests (operator, 2026-10-12;
 *    a booked upcoming event is refused "cancel first", and the dialog then offers Cancel).
 *  - Restore: an archived event (the Archive page and its drawer), with events.delete.
 *  - Reopen as draft: a cancelled event that has not started, with events.delete.
 * The server refuses the same cases; this only keeps the menus from offering a door it will shut.
 *
 * Data: partial. Ops: discardEvent, cancelEvent, updateEvent (status archived), restoreEvent,
 * reopenEvent, bulkArchiveEvents — none yet observed on the wire in the design environment.
 */
export type LifecycleKind = "archive" | "restore" | "reopen";

export function bookedCount(ev: UIEvent): number {
  return (ev.confirmed ?? ev.registered ?? 0) as number;
}

export function isArchived(ev: UIEvent): boolean {
  return (ev.raw as { status?: string } | undefined)?.status === "archived";
}

function startOf(ev: UIEvent): Date | null {
  const raw = ev.raw as { startsAt?: string; start_utc?: string } | undefined;
  const s = raw?.startsAt ?? raw?.start_utc;
  const d = s ? new Date(s) : null;
  return d && !isNaN(d.getTime()) ? d : null;
}

export function lifecycleOf(ev: UIEvent, now: Date = new Date()) {
  const archived = isArchived(ev);
  const ended = ev.status === "past" && !archived;
  const discard = !archived && ev.status === "draft" && bookedCount(ev) === 0;
  const cancel = !archived && !discard && ev.status !== "canceled" && ev.status !== "past";
  const start = startOf(ev);
  return {
    discard,
    cancel,
    archive: ended || (!archived && ev.status === "upcoming" && bookedCount(ev) === 0),
    restore: archived,
    reopen: !archived && ev.status === "canceled" && !!start && start.getTime() > now.getTime(),
  };
}

type MenuItem = { icon: ReactElement; label: string; sub?: string; danger?: boolean; onClick: () => void };

/**
 * The lifecycle menu entries for one event, shared by the Events row menu, the event drawer and
 * the Archive page so they offer the same actions. `gateCancel` keeps each surface's existing
 * gate on Cancel / Delete draft (the drawer requires events.delete; the row menu never did).
 */
export function lifecycleItems(ev: UIEvent, t: (k: string, f: string) => string, open: { cancel: () => void; kind: (k: LifecycleKind) => void }, gateCancel: boolean): MenuItem[] {
  const s = lifecycleOf(ev);
  const items: MenuItem[] = [];
  const cancelAllowed = !gateCancel || can("events.delete");
  if (s.discard && cancelAllowed) items.push({ icon: <Icon.trash size={14} />, label: t("events.inlineTable.actions.deleteDraft", "Delete draft"), danger: true, onClick: open.cancel });
  if (s.cancel && cancelAllowed) items.push({ icon: <Icon.x size={14} />, label: t("events.inlineTable.actions.cancelEvent", "Cancel event"), danger: true, sub: t("events.lifecycle.cancelSub", "You choose whether booked guests are told"), onClick: open.cancel });
  if (s.archive && can("events.write")) items.push({ icon: <Icon.archive size={14} />, label: t("events.archive.action", "Archive"), sub: t("events.archive.sub", "Moves it to Archive"), onClick: () => open.kind("archive") });
  if (s.reopen && can("events.delete")) items.push({ icon: <Icon.edit size={14} />, label: t("events.reopen.action", "Reopen as draft"), onClick: () => open.kind("reopen") });
  if (s.restore && can("events.delete")) items.push({ icon: <Icon.refresh size={14} />, label: t("events.restore.action", "Restore"), sub: t("events.restore.sub", "Back to your event lists"), onClick: () => open.kind("restore") });
  return items;
}

/** The confirmation in front of Archive, Restore and Reopen as draft. Busy on the pressed button,
 *  no second submit, the server's message kept in the dialog on a refusal, success only after a 200. */
export function EventLifecycleConfirm({ ev, kind, nav, onClose, fromDrawer, onCancelInstead }: { ev: UIEvent | null; kind: LifecycleKind | null; nav: NavApi; onClose: () => void; fromDrawer?: boolean; /** Archive refused "cancel first": open the cancel flow with the event as re-read now. */ onCancelInstead?: (fresh: UIEvent) => void }) {
  const t = useT();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [needsCancel, setNeedsCancel] = useState(false);
  const [reading, setReading] = useState(false);
  const open = !!ev && !!kind;
  const close = () => { if (!busy && !reading) { setError(null); setNeedsCancel(false); onClose(); } };
  /* The refusal means guests booked after the row was loaded, so the loaded count is stale. Re-read the
     event before the cancel opens, so its count, Notify choice and cancel-vs-discard are decided on the
     event as it is now. A failed read keeps this dialog open; the cancel never opens on the stale row. */
  const cancelInstead = async () => {
    if (!ev || reading) return;
    setReading(true);
    try {
      const fresh = await eventActions.readEvent(ev);
      setReading(false); setError(null); setNeedsCancel(false);
      onClose(); onCancelInstead?.(fresh);
    } catch (e) {
      console.warn("getStudioEvent (cancel instead of archive) failed:", e);
      setError(t("events.archive.rereadFailed", "Couldn't check who is booked now. Retry in a moment."));
      setReading(false);
    }
  };
  const run = async () => { // awaits the op; the dialog stays open on a refusal
    if (!ev || !kind || busy) return;
    setBusy(true); setError(null); setNeedsCancel(false);
    try {
      if (kind === "archive") await eventActions.archiveEvent(ev, nav);
      else if (kind === "restore") await eventActions.restoreEvent(ev, nav);
      else await eventActions.reopenEvent(ev, nav, { reopenDrawer: !!fromDrawer });
      setBusy(false); onClose();
    } catch (e) {
      const status = (e as { status?: number } | null)?.status;
      setError(kind === "restore" && status === 409
        ? t("events.restore.conflict", "This event changed while it was being restored. Reload and try again.")
        : serverMessage(e) || t("events.lifecycle.failed", "The event is unchanged. Retry in a moment."));
      /* The booked-guests refusal names path `status` on the archive doors (JAI-2608). */
      const path = (e as { fields?: readonly { path?: string }[] } | null)?.fields?.[0]?.path;
      /* cancelEvent needs events.delete; without it the refusal message stands on its own. */
      setNeedsCancel(kind === "archive" && path === "status" && !!onCancelInstead && can("events.delete"));
      setBusy(false);
    }
  };
  const copy = kind === "archive" ? {
    title: t("events.archive.title", "Archive this event?"),
    body: t("events.archive.body", "It leaves the event lists and moves to Archive. You can restore it from there."),
    confirm: t("events.archive.confirm", "Archive event"), keep: t("events.archive.keep", "Keep event"),
  } : kind === "restore" ? {
    title: t("events.restore.title", "Restore this event?"),
    body: t("events.restore.body", "The event returns to your event lists. Guests come back as they were; nobody is notified. An upcoming event comes back as a draft, so you can check it and publish it again."),
    confirm: t("events.restore.confirm", "Restore event"), keep: t("events.restore.keep", "Keep archived"),
  } : {
    title: t("events.reopen.title", "Reopen this event as a draft?"),
    body: t("events.reopen.body", "The event becomes a draft again. Guests whose registrations were cancelled stay cancelled and are not notified. Publish the draft when you are ready to take registrations."),
    confirm: t("events.reopen.confirm", "Reopen as draft"), keep: t("events.reopen.keep", "Keep cancelled"),
  };
  return (
    <ConfirmDialog open={open} tone={kind === "archive" ? "warning" : "neutral"} busy={busy} testid={`event-${kind ?? "lifecycle"}-confirm`}
      title={copy.title} body={copy.body} consequence={error ?? undefined}
      confirmLabel={copy.confirm} cancelLabel={copy.keep}
      onCancel={close} onConfirm={() => { void run(); }}>
      {needsCancel ? (
        <div style={{ marginBottom: 10 }}>
          <Btn variant="danger" size="sm" testid="event-archive-cancel-instead" disabled={reading} onClick={() => { void cancelInstead(); }}>
            {t("events.archive.needsCancel", "Cancel event")}
          </Btn>
        </div>
      ) : null}
    </ConfirmDialog>
  );
}
